import React, { useState, useEffect, useMemo } from 'react';
import { Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import ApiService from 'helpers/apiService';

interface Access {
  id: string;
  description: string;
  // category — добавлено backend'ом 2026-05-18 (RFC-005 Phase 1) для группировки
  // в UI. Поле опционально на случай старого admin-api без расширения.
  category?: string;
}

const FALLBACK_CATEGORY = 'Прочее';

const ProfilesList: React.FC = () => {
  const [accessList, setAccessList] = useState<Access[]>([]);
  const [rolesList, setRolesList] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [currentRole, setCurrentRole] = useState<any | null>(null);
  const [selectedAccessIds, setSelectedAccessIds] = useState<string[]>([]);
  const [tab, setTab] = useState('roles');

  const requestId = '4b9f0e28-0131-4a18-8c5d-e9eed02e0273';

  // Группируем accessList по category. Дедупликация по id — защита от
  // backend, который может вернуть дубли (старый формат без category,
  // двойной mount в StrictMode, гонка fetch'ей и т.п.).
  // Если дубли обнаружены — пишем в console.warn для диагностики.
  const accessByCategory = useMemo(() => {
    const groups = new Map<string, Access[]>();
    const seenIds = new Set<string>();
    const duplicates: string[] = [];
    accessList.forEach((a) => {
      if (!a.id) return;
      if (seenIds.has(a.id)) {
        duplicates.push(a.id);
        return;
      }
      seenIds.add(a.id);
      const cat = a.category || FALLBACK_CATEGORY;
      const arr = groups.get(cat) ?? [];
      arr.push(a);
      groups.set(cat, arr);
    });
    if (duplicates.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `[RolesPage] Backend вернул ${duplicates.length} дублей access-id в /profile/v1/list_access (отфильтрованы):`,
        duplicates,
      );
    }
    return Array.from(groups.entries());
  }, [accessList]);

  // accessList грузим ВСЕГДА (а не только на вкладке «Доступы») — он нужен
  // форме редактирования роли на вкладке «Роли». Раньше при заходе на «Роли»
  // и клике «Ред.» чек-лист доступов был пуст → нельзя было добавить право
  // в роль (в т.ч. в админскую). rolesList — на вкладке «Роли».
  useEffect(() => {
    ApiService.getListAccess(requestId)
      .then(response => setAccessList(response.data))
      .catch(error => console.error('Error fetching access list:', error));
  }, [requestId]);

  useEffect(() => {
    if (tab === 'groups') {
      ApiService.getListRoles(requestId)
        .then(response => setRolesList(response.data))
        .catch(error => console.error('Error fetching roles list:', error));
    }
  }, [tab, requestId]);

  const handleAddRole = (data: any) => {
    ApiService.addRole(data, requestId)
      .then(() => {
        setShowForm(false);
        setCurrentRole(null);
        ApiService.getListRoles(requestId).then(response => setRolesList(response.data));
      })
      .catch(error => console.error('Error adding role:', error));
  };

  const handleEditRole = (data: any) => {
    ApiService.editRole(data, requestId)
      .then(() => {
        setShowForm(false);
        setCurrentRole(null);
        ApiService.getListRoles(requestId).then(response => setRolesList(response.data));
      })
      .catch(error => console.error('Error editing role:', error));
  };

  const handleDeleteRole = (idRole: string) => {
    ApiService.deleteRole(idRole, requestId)
      .then(() => setRolesList(rolesList.filter(role => role.id !== idRole)))
      .catch(error => console.error('Error deleting role:', error));
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    if (currentRole) {
      handleEditRole({ id: currentRole.id, description: formData.get('description'), access: selectedAccessIds });
    } else {
      handleAddRole({ description: formData.get('description'), access: selectedAccessIds });
    }
  };

  const handleEditButtonClick = (role: any) => {
    setCurrentRole(role);
    setSelectedAccessIds(role.access);
    setShowForm(true);
  };

  const handleCancel = () => {
    setCurrentRole(null);
    setSelectedAccessIds([]);
    setShowForm(false);
  };

  const handleAccessChange = (id: string) => {
    setSelectedAccessIds(prev => {
      const safe = prev || [];
      return safe.includes(id) ? safe.filter(a => a !== id) : [...safe, id];
    });
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <h3 className="mb-0">Справочники</h3>
          <p className="mt-1 mb-0 text-muted small">Управление ролями и доступами</p>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <div className="d-flex gap-2">
            <Button
              variant={tab === 'roles' ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => setTab('roles')}
            >
              Доступы
            </Button>
            <Button
              variant={tab === 'groups' ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => setTab('groups')}
            >
              Роли
            </Button>
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {tab === 'roles' && (
            <Table hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: '0.82rem' }}>
              <thead className="table-light">
                <tr>
                  <th>ID доступа</th>
                  <th>Описание</th>
                </tr>
              </thead>
              <tbody>
                {accessByCategory.map(([cat, list]) => (
                  <React.Fragment key={cat}>
                    <tr className="table-secondary">
                      <td colSpan={2} className="fw-semibold" style={{ fontSize: '0.8rem' }}>
                        {cat} <span className="text-muted fw-normal">({list.length})</span>
                      </td>
                    </tr>
                    {list.map((access) => (
                      <tr key={access.id}>
                        <td><code style={{ fontSize: '0.75rem' }}>{access.id}</code></td>
                        <td>{access.description}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                {accessList.length === 0 && (
                  <tr><td colSpan={2} className="text-muted text-center py-3">Нет данных</td></tr>
                )}
              </tbody>
            </Table>
          )}

          {tab === 'groups' && (
            <>
              <div className="d-flex justify-content-end p-3 pb-0">
                <Button variant="primary" size="sm" onClick={() => setShowForm(true)}>
                  + Добавить группу
                </Button>
              </div>
              <Table hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: '0.82rem' }}>
                <thead className="table-light">
                  <tr>
                    <th>ID Группы</th>
                    <th>Описание</th>
                    <th>Доступы</th>
                    <th style={{ width: 110 }}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {rolesList.map((role, index) => (
                    <tr key={index}>
                      <td><code style={{ fontSize: '0.75rem' }}>{role.id}</code></td>
                      <td>{role.description}</td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {role?.access?.map((a: string) => (
                            <Badge key={a} bg="light" text="dark" style={{ fontSize: '0.7rem' }}>{a}</Badge>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button variant="outline-secondary" size="sm" style={{ fontSize: '0.7rem', padding: '2px 6px' }} onClick={() => handleEditButtonClick(role)}>
                            Ред.
                          </Button>
                          <Button variant="outline-danger" size="sm" style={{ fontSize: '0.7rem', padding: '2px 6px' }} onClick={() => handleDeleteRole(role.id)}>
                            Del
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {rolesList.length === 0 && (
                    <tr><td colSpan={4} className="text-muted text-center py-3">Нет данных</td></tr>
                  )}
                </tbody>
              </Table>
            </>
          )}
        </Card.Body>
      </Card>

      {showForm && (
        <Card className="mt-3">
          <Card.Header>
            <Card.Title className="mb-0 fs-6">
              {currentRole ? 'Редактировать группу' : 'Добавить группу'}
            </Card.Title>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleFormSubmit}>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small">ID Группы</Form.Label>
                    <Form.Control
                      size="sm"
                      type="text"
                      name="idRole"
                      value={currentRole ? currentRole.id : ''}
                      placeholder="Назначится автоматически"
                      disabled
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small">Описание <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      size="sm"
                      type="text"
                      name="description"
                      defaultValue={currentRole ? currentRole.description : ''}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Label className="small">Доступы</Form.Label>
                  <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 4, padding: '8px 12px' }}>
                    {accessByCategory.map(([cat, list]) => (
                      <div key={cat} className="mb-2">
                        <div className="fw-semibold text-uppercase text-muted" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>
                          {cat}
                        </div>
                        {list.map((access) => (
                          <Form.Check
                            key={access.id}
                            type="checkbox"
                            id={`access-${access.id}`}
                            label={<span style={{ fontSize: '0.8rem' }}>{access.description}</span>}
                            checked={selectedAccessIds?.includes(access.id)}
                            onChange={() => handleAccessChange(access.id)}
                          />
                        ))}
                      </div>
                    ))}
                    {accessList.length === 0 && (
                      <div className="text-muted text-center py-2">Список доступов пуст</div>
                    )}
                  </div>
                </Col>
              </Row>
              <div className="d-flex justify-content-end mt-3 gap-2">
                <Button variant="outline-secondary" size="sm" onClick={handleCancel}>
                  Отмена
                </Button>
                <Button variant="primary" size="sm" type="submit">
                  Сохранить
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default ProfilesList;
